import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/** Ease-out cubic — fast off the mark, settling gently into the final value. */
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

/**
 * Animates a number towards `target`, returning the current value each frame.
 *
 * Animates from wherever it currently is: 0 on mount (so a value rises into
 * place), or the previously displayed value when `target` changes (so a
 * refreshed figure glides rather than jumping). A target change mid-flight
 * retargets from the current frame — no snap-back.
 *
 * Honours `prefers-reduced-motion`: the target is returned immediately, with no
 * frame loop. (jsdom reports reduced-motion ON via the test setup, so components
 * using this render their final values synchronously under test.)
 */
export function useCountUp(target: number, durationMs = 700, delayMs = 0): number {
  const reduced = useReducedMotion();
  const [animated, setAnimated] = useState(0);
  const shownRef = useRef(0);

  // Reduced motion renders `target` directly (no frame loop), so keep the
  // animation state in sync DURING RENDER (the sanctioned adjust-state
  // pattern) — otherwise turning the preference back OFF mid-session would
  // return the stale `animated` (0 on a fresh mount) forever, since the
  // effect's early return never re-animates a ref already at `target`.
  if (reduced && animated !== target) {
    setAnimated(target);
  }

  useEffect(() => {
    // Reduced motion: no frame loop; the render path above keeps state honest.
    if (reduced) {
      shownRef.current = target;
      return;
    }
    const from = shownRef.current;
    if (from === target) return;

    let frameId = 0;
    let startTs = 0;
    const step = (ts: number) => {
      if (startTs === 0) startTs = ts;
      const progress = durationMs > 0 ? Math.min(1, (ts - startTs) / durationMs) : 1;
      const next = from + (target - from) * easeOutCubic(progress);
      shownRef.current = next;
      setAnimated(next);
      if (progress < 1) frameId = requestAnimationFrame(step);
    };

    const timerId = window.setTimeout(() => {
      frameId = requestAnimationFrame(step);
    }, delayMs);

    return () => {
      window.clearTimeout(timerId);
      cancelAnimationFrame(frameId);
    };
  }, [target, durationMs, delayMs, reduced]);

  return reduced ? target : animated;
}
